import React, { useState } from 'react';
import { CaretDown } from '@phosphor-icons/react';

/**
 * Light-on-dark accordion used inside the registration page over the
 * themed hero background. Renders a section's heading + a body composed
 * of intro paragraph + bullets + body paragraphs as defined in
 * `legalContent.js`.
 *
 * Pass `theme="light"` to render on light backgrounds (e.g. inside the
 * standalone /privacy, /terms, /sms-terms shells).
 */
export default function LegalAccordion({ title, sections, defaultOpen = false, testId, theme = 'dark' }) {
  const [open, setOpen] = useState(defaultOpen);
  const isDark = theme === 'dark';

  const surfaceCls = isDark
    ? 'bg-white/5 border-white/15 text-white/85 hover:bg-white/10'
    : 'bg-[#fbfaf8] border-[#e3dcd1] text-[#013523] hover:bg-[#f5f0e6]';
  const headingCls = isDark ? 'text-white' : 'text-[#013523]';
  const subHeadingCls = isDark ? 'text-white/90' : 'text-[#013523]';
  const bodyCls = isDark ? 'text-white/75' : 'text-[#2a332c]';
  const dividerCls = isDark ? 'border-white/10' : 'border-[#e3dcd1]';
  const bulletCls = isDark ? 'marker:text-white/40' : 'marker:text-[#015e3f]';

  return (
    <div
      data-testid={testId}
      className={`rounded-xl border transition-colors ${surfaceCls}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <span className={`font-serif text-base sm:text-lg tracking-tight ${headingCls}`}>{title}</span>
        <CaretDown
          size={18}
          weight="bold"
          className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${headingCls}`}
        />
      </button>
      {open && (
        <div className={`px-4 pb-4 pt-1 border-t ${dividerCls} space-y-4 text-sm leading-relaxed ${bodyCls}`}>
          {sections.map((s, i) => (
            <div key={i} className="space-y-2">
              {s.heading && (
                <h3 className={`font-serif text-[15px] sm:text-base ${subHeadingCls}`}>{s.heading}</h3>
              )}
              {s.intro && <p>{s.intro}</p>}
              {s.bullets && (
                <ul className={`list-disc pl-5 space-y-1.5 ${bulletCls}`}>
                  {s.bullets.map((b, j) => <li key={j}>{b}</li>)}
                </ul>
              )}
              {s.body && <p>{s.body}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
